import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const BAR_COUNT_DEFAULT = 24;
const BAR_COUNT_HERO = 11;
const IDLE_BAR_HEIGHT = 0.06;

type LiveAudioWaveVisualizerProps = {
	stream: MediaStream | null;
	className?: string;
	ariaLabel?: string;
	active?: boolean;
	inactive?: boolean;
	compact?: boolean;
	variant?: 'default' | 'hero';
	onAudioLevel?: (level: number) => void;
};

type WindowWithWebkitAudioContext = Window & {
	webkitAudioContext?: typeof AudioContext;
};

const resolveAudioContextClass = (): typeof AudioContext | undefined =>
	window.AudioContext ||
	(window as WindowWithWebkitAudioContext).webkitAudioContext;

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value));

const LiveAudioWaveVisualizer = ({
	stream,
	className,
	ariaLabel = 'Visualização de áudio',
	active = true,
	inactive = false,
	compact = false,
	variant = 'default',
	onAudioLevel,
}: LiveAudioWaveVisualizerProps) => {
	const isHero = variant === 'hero';
	const barCount = isHero ? BAR_COUNT_HERO : BAR_COUNT_DEFAULT;
	const onAudioLevelRef = useRef(onAudioLevel);

	onAudioLevelRef.current = onAudioLevel;

	const [levels, setLevels] = useState<number[]>(() =>
		Array.from({ length: barCount }, () => IDLE_BAR_HEIGHT),
	);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const audioContextRef = useRef<AudioContext | null>(null);
	const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
	const rafRef = useRef<number | null>(null);
	const dataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
	const previousLevelsRef = useRef<number[]>(
		Array.from({ length: barCount }, () => IDLE_BAR_HEIGHT),
	);

	useEffect(() => {
		if (!stream || !active || inactive) {
			if (rafRef.current != null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			sourceRef.current?.disconnect();
			sourceRef.current = null;
			analyserRef.current?.disconnect();
			analyserRef.current = null;
			if (audioContextRef.current) {
				void audioContextRef.current.close();
				audioContextRef.current = null;
			}
			setLevels(Array.from({ length: barCount }, () => IDLE_BAR_HEIGHT));
			previousLevelsRef.current = Array.from(
				{ length: barCount },
				() => IDLE_BAR_HEIGHT,
			);
			return;
		}

		const AudioContextClass = resolveAudioContextClass();
		if (!AudioContextClass || stream.getAudioTracks().length === 0) {
			return;
		}

		const audioContext = new AudioContextClass();
		const analyser = audioContext.createAnalyser();
		analyser.fftSize = 256;
		analyser.smoothingTimeConstant = 0.68;
		const source = audioContext.createMediaStreamSource(stream);
		source.connect(analyser);

		audioContextRef.current = audioContext;
		analyserRef.current = analyser;
		sourceRef.current = source;
		dataRef.current = new Uint8Array(
			new ArrayBuffer(analyser.frequencyBinCount),
		);

		const tick = () => {
			const node = analyserRef.current;
			const buffer = dataRef.current;
			if (!node || !buffer) return;

			node.getByteFrequencyData(buffer);
			const step = Math.max(1, Math.floor(buffer.length / barCount));
			const nextLevels = Array.from({ length: barCount }, (_, index) => {
				const start = index * step;
				const end = Math.min(buffer.length, start + step);
				let sum = 0;
				for (let i = start; i < end; i += 1) {
					sum += buffer[i];
				}
				const average = sum / Math.max(1, end - start);
				const normalized = clamp(average / 170, 0, 1);
				const eased = normalized ** 0.7;
				const previous =
					previousLevelsRef.current[index] ?? IDLE_BAR_HEIGHT;
				const blended = previous * 0.38 + eased * 0.62;
				return Math.max(IDLE_BAR_HEIGHT, blended);
			});

			previousLevelsRef.current = nextLevels;
			setLevels(nextLevels);
			if (onAudioLevelRef.current) {
				const peak = nextLevels.reduce(
					(max, value) => (value > max ? value : max),
					IDLE_BAR_HEIGHT,
				);
				onAudioLevelRef.current(peak);
			}
			rafRef.current = requestAnimationFrame(tick);
		};

		void audioContext.resume();
		rafRef.current = requestAnimationFrame(tick);

		return () => {
			if (rafRef.current != null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			source.disconnect();
			analyser.disconnect();
			void audioContext.close();
			audioContextRef.current = null;
			analyserRef.current = null;
			sourceRef.current = null;
		};
	}, [active, barCount, inactive, stream]);

	const maxBarPx = isHero ? 28 : compact ? 18 : 36;
	const barColor = inactive
		? 'bg-zinc-500'
		: isHero
		  ? 'bg-[#38bdf8]'
		  : 'bg-[#8ab4f8]';

	return (
		<div
			role="img"
			aria-label={ariaLabel}
			className={cn(
				'flex w-full items-center justify-center',
				isHero ? 'h-[5.5rem]' : compact ? 'h-10' : 'h-14',
				inactive && 'opacity-40',
				className,
			)}
		>
			{levels.map((level, index) => {
				const halfHeight = Math.round(4 + level * maxBarPx);
				const totalHeight = halfHeight * 2;

				return (
					<span
						key={index}
						className="flex flex-1 items-center justify-center"
						style={{
							height: maxBarPx * 2 + 4,
							minWidth: isHero ? 3 : 2,
						}}
					>
						<span
							className={cn(
								'w-full rounded-[1px] transition-[height] duration-75 ease-out',
								isHero ? 'max-w-[5px]' : 'max-w-[3px]',
								barColor,
							)}
							style={{ height: totalHeight }}
						/>
					</span>
				);
			})}
		</div>
	);
};

export { LiveAudioWaveVisualizer };
